import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import {
  DEFAULT_GENERATOR_SCRIPTS,
  GENERATORS,
  GENERATOR_KEYS,
  InterchangeError,
  JAVASCRIPT,
  JavaRandom,
  NEW_SCRIPT_SOURCE,
  SCRIPT_NAME_MAX,
  languageName,
  readScriptPack,
  writeScriptPack,
  type GeneratedEntry,
  type GeneratorKey,
  type ScriptPack,
  type ScriptPackRead,
} from '@realmery/realm-core';
import { realms } from '../../db/client.ts';
import { realmFile } from '../../db/protocol.ts';
import { saveBytes } from '../../files.ts';
import { freshSeed } from '../../scripting/scriptRunner.ts';
import { settings } from '../../settings.ts';
import { message } from '../sheets/SheetsPage.tsx';
import { PackImport } from './PackImport.tsx';

/**
 * Generators and scripts (docs/PARITY-STEP-2-PLAN.md section 6), as the desktop's Generators
 * & Scripting view: the seven Quick Generators, each result ready to create as an entry; the
 * realm's saved scripts (JavaScript ones written here, Python ones from the desktop, shown);
 * New script, and Import and Export of script packs.
 */

const route = getRouteApi('/_app/realm/$realmKey/generators');
const MAX_FILE = 2 * 1024 * 1024;

/** "notableFeature" -> "NotableFeature", as the desktop labels a generator's fields. */
const capitalize = (text: string) => (text === '' ? text : text[0]!.toUpperCase() + text.slice(1));

/** A field's value as the desktop writes it: lists as "[a, b]". */
export const fieldText = (value: unknown) => (Array.isArray(value) ? `[${value.join(', ')}]` : String(value));

/** A generator's result as an entry: its title and description, its other fields as the entry's fields. */
export function generatedEntry(entityType: string, data: Readonly<Record<string, unknown>>): GeneratedEntry {
  return {
    entityType,
    title: fieldText(data['title'] ?? 'Generated Entry'),
    description: fieldText(data['description'] ?? ''),
    labels: [],
    metadata: Object.entries(data)
      .filter(([key]) => key !== 'title' && key !== 'description' && key !== 'type')
      .map(([key, value]) => [capitalize(key), fieldText(value)] as const),
  };
}

export function GeneratorsPage() {
  const { scripts, overrides } = route.useLoaderData();
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<'none' | 'new' | 'import' | 'export'>('none');
  const [read, setRead] = useState<{ read: ScriptPackRead; from: string } | null>(null);
  const [error, setError] = useState('');

  async function act(work: () => Promise<void>, fallback: string) {
    setError('');
    try {
      await work();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  const openScript = (scriptId: string) => void navigate({ to: '/realm/$realmKey/scripts/$scriptId', params: { realmKey, scriptId } });

  async function readPack(text: string, from: string) {
    await act(async () => {
      setRead({ read: readScriptPack(text), from });
      setPanel('import');
    }, 'The file could not be read.');
  }

  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);

  async function remove(id: string) {
    setDeleting(null);
    await act(async () => {
      await realms.deleteScript(file, id);
      await router.invalidate();
    }, 'The script could not be deleted.');
  }

  return (
    <article className="document generators-page">
      <p className="eyebrow">Generators and scripts</p>
      <h1>Generators and scripts</h1>
      <p className="lead">Names, cities, taverns and more in a click, and scripts of your own to fill a realm: they run in a sandbox, and nothing is created until you choose it.</p>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      <QuickGenerators file={file} realmKey={realmKey} overrides={overrides} />

      <h2>Scripts</h2>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setPanel(panel === 'new' ? 'none' : 'new')}>
          New script
        </button>
        <label className="button file-button">
          Import…
          <input
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const chosen = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (!chosen) return;
              if (chosen.size > MAX_FILE) setError('That file is too large to be a script pack (over 2 MB).');
              else void chosen.text().then((text) => readPack(text, chosen.name));
            }}
          />
        </label>
        <button
          type="button"
          className="quiet"
          onClick={() =>
            void act(async () => {
              const response = await fetch('/examples/asterian-script-pack.json');
              if (!response.ok) throw new InterchangeError('The example could not be fetched. Check the connection and try again.');
              await readPack(await response.text(), 'the example pack');
            }, 'The example could not be fetched.')
          }
        >
          Try the example pack
        </button>
        {(scripts.length > 0 || Object.keys(overrides).length > 0) && (
          <button type="button" className="quiet" onClick={() => setPanel(panel === 'export' ? 'none' : 'export')}>
            Export…
          </button>
        )}
      </div>

      {panel === 'new' && (
        <NewScript
          onCancel={() => setPanel('none')}
          onCreate={(name, description, source) =>
            act(async () => {
              const { id } = await realms.createScript(file, name, description, source);
              openScript(id);
            }, 'The script could not be made.')
          }
        />
      )}
      {panel === 'import' && read && (
        <PackImport
          read={read.read}
          from={read.from}
          onCancel={() => setPanel('none')}
          onImport={(pack: ScriptPack) =>
            act(async () => {
              const { scriptIds } = await realms.importScriptPack(file, pack);
              setPanel('none');
              setRead(null);
              await router.invalidate();
              if (scriptIds.length === 1 && pack.generators.length === 0) openScript(scriptIds[0]!);
            }, 'The pack could not be imported.')
          }
        />
      )}
      {panel === 'export' && (
        <ExportPack
          file={file}
          scripts={scripts.map((s) => ({ id: s.id, name: s.name, language: s.language }))}
          overrides={GENERATOR_KEYS.filter((key) => overrides[key]).map((key) => ({ key, source: overrides[key]!.source }))}
          onDone={() => setPanel('none')}
          onError={setError}
        />
      )}

      {deleting && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${deleting.name}`}>
          <p>Delete the script “{deleting.name}”? This can't be undone.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove(deleting.id)}>
              Delete script
            </button>
            <button type="button" onClick={() => setDeleting(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {scripts.length === 0 ? (
        <p className="meta">No scripts yet. New script starts one, from scratch or from any of the generators.</p>
      ) : (
        <ul className="card-gallery">
          {scripts.map((script) => (
            <li key={script.id} className="card">
              <Link className="card-title" to="/realm/$realmKey/scripts/$scriptId" params={{ realmKey, scriptId: script.id }}>
                {script.name}
              </Link>
              <span className="meta">{script.language === JAVASCRIPT ? 'JavaScript' : `${languageName(script.language)} · runs in the desktop app`}</span>
              {script.description && <span className="meta">{script.description}</span>}
              <div className="actions">
                <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setDeleting({ id: script.id, name: script.name }) : void remove(script.id))}>
                  Delete…
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function QuickGenerators({ file, realmKey, overrides }: { file: string; realmKey: string; overrides: Readonly<Partial<Record<GeneratorKey, { source: string }>>> }) {
  const router = useRouter();
  const [result, setResult] = useState<{ key: GeneratorKey; data: Record<string, unknown> } | null>(null);
  const [created, setCreated] = useState<{ id: string; title: string } | null>(null);
  const [error, setError] = useState('');

  function run(key: GeneratorKey) {
    setCreated(null);
    setError('');
    setResult({ key, data: GENERATORS[key].generate(new JavaRandom(BigInt(freshSeed()))) });
  }

  async function create() {
    if (!result) return;
    try {
      const entry = generatedEntry(GENERATORS[result.key].entityType, result.data);
      const { ids } = await realms.createGeneratedEntries(file, [entry]);
      setCreated({ id: ids[0]!, title: entry.title });
      setResult(null);
      // The sidebar lists the new entry.
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'The entry could not be made.'));
    }
  }

  return (
    <section className="panel quick-generators" aria-label="Quick generators">
      <h2>✨ Quick generators</h2>
      <div className="actions">
        {GENERATOR_KEYS.map((key) => (
          <button key={key} type="button" title={`Run the ${GENERATORS[key].name} generator and see what it makes`} onClick={() => run(key)}>
            {GENERATORS[key].name}
          </button>
        ))}
      </div>
      {result ? (
        <div className="generated" role="status">
          <dl className="fields">
            {Object.entries(result.data).map(([key, value]) => (
              <div key={key}>
                <dt>{capitalize(key)}</dt>
                <dd>{fieldText(value)}</dd>
              </div>
            ))}
          </dl>
          <div className="actions">
            <button type="button" className="primary" onClick={() => void create()}>
              + Create as {GENERATORS[result.key].entityType.toLowerCase()} entry
            </button>
            <button type="button" onClick={() => run(result.key)}>
              Again
            </button>
          </div>
        </div>
      ) : (
        <p className="meta">Pick a generator to see a result.</p>
      )}
      {created && (
        <p className="notice" role="status">
          Created{' '}
          <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: created.id }} search={{}}>
            {created.title}
          </Link>
          .
        </p>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {GENERATOR_KEYS.some((key) => overrides[key]) && (
        <details className="desktop-generators">
          <summary>Generators changed in the desktop app</summary>
          <p className="meta">
            The desktop app runs its own Python versions of these. Here they run as built in; their Python is kept as it is, and exports with the realm's scripts.
          </p>
          {GENERATOR_KEYS.filter((key) => overrides[key]).map((key) => (
            <div key={key}>
              <h3>{GENERATORS[key].name}</h3>
              <pre className="code-view">{overrides[key]!.source}</pre>
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

function NewScript({ onCreate, onCancel }: { onCreate: (name: string, description: string, source: string) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState<'blank' | GeneratorKey>('blank');
  return (
    <form
      className="panel"
      aria-label="New script"
      onSubmit={(e) => {
        e.preventDefault();
        void onCreate(name, description, start === 'blank' ? NEW_SCRIPT_SOURCE : DEFAULT_GENERATOR_SCRIPTS[start]);
      }}
    >
      <label>
        Name
        <input value={name} maxLength={SCRIPT_NAME_MAX} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <input value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <label>
        Start from
        <select value={start} onChange={(e) => setStart(e.currentTarget.value as 'blank' | GeneratorKey)}>
          <option value="blank">An example that makes three cities</option>
          {GENERATOR_KEYS.map((key) => (
            <option key={key} value={key}>
              The {GENERATORS[key].name} generator
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button type="submit" className="primary">
          Make script
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ExportPack({
  file,
  scripts,
  overrides,
  onDone,
  onError,
}: {
  file: string;
  scripts: { id: string; name: string; language: string }[];
  overrides: { key: GeneratorKey; source: string }[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(() => new Set([...scripts.map((s) => s.id), ...overrides.map((o) => o.key)]));
  const toggle = (id: string) =>
    setChosen((all) => {
      const next = new Set(all);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  async function save() {
    try {
      const full = await Promise.all(scripts.filter((s) => chosen.has(s.id)).map((s) => realms.getScript(file, s.id)));
      const pack: ScriptPack = {
        generators: overrides.filter((o) => chosen.has(o.key)),
        scripts: full.filter((s) => s !== null).map((s) => ({ name: s.name, description: s.description, source: s.source, language: s.language })),
      };
      const name = pack.generators.length === 0 && pack.scripts.length === 1 ? pack.scripts[0]!.name : 'Realmery Scripts';
      saveBytes(new TextEncoder().encode(writeScriptPack(pack, 'Realmery Web')), `${name.replace(/[^\w -]+/g, '').trim() || 'scripts'}.json`, 'application/json');
      onDone();
    } catch (e) {
      onError(message(e, 'The scripts could not be exported.'));
    }
  }

  return (
    <div className="panel" role="group" aria-label="Export scripts">
      <p className="meta">Save scripts as one JSON file, to import into another realm on the web or the desktop.</p>
      {scripts.map((s) => (
        <label key={s.id} className="check">
          <input type="checkbox" checked={chosen.has(s.id)} onChange={() => toggle(s.id)} />
          <span>
            {s.name} <span className="meta">({s.language === JAVASCRIPT ? 'JavaScript' : languageName(s.language)})</span>
          </span>
        </label>
      ))}
      {overrides.map((o) => (
        <label key={o.key} className="check">
          <input type="checkbox" checked={chosen.has(o.key)} onChange={() => toggle(o.key)} />
          <span>
            The desktop's {GENERATORS[o.key].name} generator <span className="meta">(Python)</span>
          </span>
        </label>
      ))}
      <div className="actions">
        <button type="button" className="primary" disabled={chosen.size === 0} onClick={() => void save()}>
          Export {chosen.size}
        </button>
        <button type="button" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}
